import React, { useMemo } from 'react';
import { 
  TextField, Checkbox, FormControlLabel, Stack, 
  Autocomplete, createFilterOptions, CircularProgress
} from '@mui/material';
import { ContactOrgLinkPayload, OrgOption, useOrganizationLink } from '../data/useOrganizationLink';

interface UnboundProps {
  value: Partial<ContactOrgLinkPayload>;
  searchValue: string;
  onSearchChange: (val: string) => void;
  availableOrganizations: OrgOption[];
  isLoading: boolean;
  onOrgChange: (val: string | OrgOption | null) => void;
  onFieldChange: (updates: Partial<ContactOrgLinkPayload>) => void;
  hideIsActive?: boolean;
  noAutoFocus?: boolean;
  isOrganizationChangeDisabled?: boolean;
}

const filter = createFilterOptions<OrgOption>();

export const ContactOrgLinkControlUnbound: React.FC<UnboundProps> = ({ 
  value, 
  searchValue, 
  onSearchChange,
  availableOrganizations, 
  isLoading,
  onOrgChange,
  onFieldChange,
  hideIsActive,
  isOrganizationChangeDisabled,
  noAutoFocus,
}) => {

  const selectedOrg = useMemo((): OrgOption | null => {
    if (value.organization_id && value.organization_name) {
      return { _id: value.organization_id, name: value.organization_name };
    }
    if (value.organization_name) {
      return { name: value.organization_name }; 
    }
    return null;
  }, [value.organization_id, value.organization_name]);

  return (
    <Stack spacing={2}>
      <Autocomplete
        disabled={isOrganizationChangeDisabled}
        value={selectedOrg}
        inputValue={searchValue}
        onInputChange={(_, newInputValue) => onSearchChange(newInputValue)}
        onChange={(_, newValue) => onOrgChange(newValue)}
        options={availableOrganizations}
        loading={isLoading}
        filterOptions={(options, params) => {
          const filtered = filter(options, params);
          const { inputValue } = params;
          
          const isExisting = options.some((option) => inputValue === option.name);
          if (inputValue !== '' && !isExisting) {
            filtered.push({
              inputValue,
              name: `Создать "${inputValue}"`,
            });
          }
          return filtered;
        }}
        selectOnFocus
        clearOnBlur
        handleHomeEndKeys
        getOptionLabel={(option) => {
          if (typeof option === 'string') return option;
          if (option.inputValue) return option.inputValue;
          return option.name;
        }}
        renderOption={(props, option) => <li {...props}>{option.name}</li>}
        freeSolo
        renderInput={(params) => (
          <TextField 
            {...params} 
            label="Компания" 
            placeholder="Выберите или введите новую..." 
            size="small"
            autoFocus={!noAutoFocus}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />

      <TextField
        label="Должность"
        size="small"
        fullWidth
        value={value.title || ''}
        onChange={(e) => onFieldChange({ title: e.target.value })}
      />

      {!hideIsActive && (
        <FormControlLabel
          control={
            <Checkbox 
              checked={value.is_active !== false} 
              onChange={(e) => onFieldChange({ is_active: e.target.checked })} 
            />
          }
          label="Сейчас там работает"
        />
      )}
    </Stack>
  );
};

// BOUND COMPONENT

export interface ContactOrgLinkControlProps {
  value: Partial<ContactOrgLinkPayload>;
  onChange: React.Dispatch<React.SetStateAction<Partial<ContactOrgLinkPayload>>>;
  hideIsActive?: boolean;
  noAutoFocus?: boolean;
  isOrganizationChangeDisabled?: boolean;
}

export const ContactOrgLinkControl: React.FC<ContactOrgLinkControlProps> = (props) => {
  const { 
    value, 
    onChange, 
  } = props;
  const hookData = useOrganizationLink(value, onChange);

  return (
    <ContactOrgLinkControlUnbound
      hideIsActive={props.hideIsActive}
      noAutoFocus={props.noAutoFocus}
      isOrganizationChangeDisabled={props.isOrganizationChangeDisabled}
      onSearchChange={hookData.setSearchValue}
      onFieldChange={hookData.handleFieldChange}
      onOrgChange={hookData.handleOrgChange}
      {...hookData}
      value={value || {}}
    />
  );
};
